import type {FieldTypeId, SimpleFond, Sorting} from 'src/typings';
import type {FieldTypeDefinition} from 'src/features/record/field-types/types';
import {exists} from 'shared/utils/custom-utils';

export type FilterSearchType = 'value' | 'target-reference' | 'origin-reference';

export enum FilterOperatorEnum {
    eqOrLess = 'eqOrLess',
    eqOrMore = 'eqOrMore',
    within = 'within'
}

export type FilterOperator = keyof typeof FilterOperatorEnum;

export interface KpSearchFieldTypeFiltersOptions {
    defaultFilterValues?: FilterValuesMap,
    forcedFilterValues?: FilterValuesMap,
    activeSorting?: Sorting | null,
}

export enum FilterableDatatype {
    BOOLEAN = 'BOOLEAN',
    DATE = 'DATE',
    DATETIME = 'DATETIME',
    DATETIME_RANGE = 'DATETIME_RANGE',
    NUMBER = 'NUMBER',
    TEXT = 'TEXT',
    CODEBOOK_VALUE = 'CODEBOOK_VALUE',
    RECORD = 'RECORD',
}

export interface FilterableFieldTypeDefinition extends FieldTypeDefinition {
    valueFilterSearchDefinition?: DatatypedFilterDefinition | null;
    originReferenceFilterSearchDefinition?: DatatypedFilterDefinition | null;
    targetReferenceFilterSearchDefinition?: DatatypedFilterDefinition | null;
}

export interface DatatypedFilterDefinition {
    fieldName: string;
    datatype: AnyFilterableDatatype;
}

export interface GenericDatatypedFilterDefinition<T extends AnyFilterableDatatype> {
    fieldName: string;
    datatype: T;
}

interface ValueDatatype {
    name: FilterableDatatype;
}

// Datatype overloads based on Java StructuredDatatypeResponse hierarchy
export interface BooleanFilterableDatatype extends ValueDatatype {
    name: FilterableDatatype.BOOLEAN;
}

export interface DateFilterableDatatype extends ValueDatatype {
    name: FilterableDatatype.DATE;
}

export interface DatetimeFilterableDatatype extends ValueDatatype {
    name: FilterableDatatype.DATETIME;
}

export interface DatetimeRangeFilterableDatatype extends ValueDatatype {
    name: FilterableDatatype.DATETIME_RANGE;
}

export interface NumberFilterableDatatype extends ValueDatatype {
    name: FilterableDatatype.NUMBER;

    min?: number | null;
    max?: number | null;
    scale?: number | null;
}

export interface TextFilterableDatatype extends ValueDatatype {
    name: FilterableDatatype.TEXT;

    blankable?: boolean | null;
    minLength?: number | null;
    maxLength?: number | null;
    pattern?: string | null;
    autocompleteDataUrl?: string | null;
}

export interface CodebookValueFilterableDatatype<VALUE = any, ID = any> extends ValueDatatype {
    name: FilterableDatatype.CODEBOOK_VALUE;

    groupId: string;
    acceptableValues: Array<VALUE & { id: ID; label: string }>;
}

export interface RecordFilterableDatatype extends ValueDatatype {
    name: FilterableDatatype.RECORD;

    rootFond?: SimpleFond[] | null;
}

export type AnyFilterableDatatype =
    BooleanFilterableDatatype
    | DateFilterableDatatype
    | DatetimeFilterableDatatype
    | DatetimeRangeFilterableDatatype
    | NumberFilterableDatatype
    | TextFilterableDatatype
    | CodebookValueFilterableDatatype
    | RecordFilterableDatatype;

export interface FilteredValue {
    val: any;
    datatype: FilterableDatatype;
    filterOperator?: FilterOperator;
}

export type FilterFieldTypeId = `field_${FieldTypeId}_${'val' | 'link' | 'origin'}`;
export type SortFieldTypeId = `field_${FieldTypeId}_sort`;
export type FilterValuesMap = Record<FilterFieldTypeId, FilteredValue>;

// Guard functions
export function hasAnyFilterSearchFieldDefinition(fieldTypeDefinition: FieldTypeDefinition): fieldTypeDefinition is FilterableFieldTypeDefinition {
    return 'valueFilterSearchDefinition' in fieldTypeDefinition
        && 'targetReferenceFilterSearchDefinition' in fieldTypeDefinition
        && 'originReferenceFilterSearchDefinition' in fieldTypeDefinition
        && (exists(fieldTypeDefinition.valueFilterSearchDefinition) || exists(fieldTypeDefinition.targetReferenceFilterSearchDefinition) || exists(fieldTypeDefinition.originReferenceFilterSearchDefinition));
}